import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import * as LucideIcons from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  Input,
  PageHeader,
  Spinner,
  useToast,
} from '@biartbt/biartd'
import type { MenuItem } from '@/services/menuAPI'
import { menuAPI } from '@/services/menuAPI'
import { useActiveTenantId } from '@/store/tenantStore'
import type { RoleItem } from '../api/rolesApi'
import { rolesApi } from '../api/rolesApi'

function getLucideIcon(iconName?: string): React.ComponentType<{ className?: string }> | null {
  if (!iconName) return null
  const Icon = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[iconName]
  return Icon || null
}

function flattenMenus(items: MenuItem[]): MenuItem[] {
  const out: MenuItem[] = []
  for (const m of items) {
    out.push(m)
    if (m.children?.length) {
      out.push(...flattenMenus(m.children))
    }
  }
  return out
}

export default function RoleMenuManagementPage() {
  const { roleId: roleIdParam } = useParams<{ roleId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const activeTenantId = useActiveTenantId()
  const tenantIdFromUrl = searchParams.get('tenantId')
  const tenantId = tenantIdFromUrl ? Number(tenantIdFromUrl) : activeTenantId

  const roleId = roleIdParam != null ? parseInt(roleIdParam, 10) : NaN
  const isValidRoleId = Number.isFinite(roleId) && roleId > 0

  const { toast } = useToast()
  const [role, setRole] = useState<RoleItem | null>(null)
  const [menus, setMenus] = useState<MenuItem[]>([])
  const [selectedMenuIds, setSelectedMenuIds] = useState<Set<number>>(new Set())
  const [loadingRole, setLoadingRole] = useState(true)
  const [loadingMenus, setLoadingMenus] = useState(true)
  const [loadingRoleMenus, setLoadingRoleMenus] = useState(true)
  const [saving, setSaving] = useState(false)
  const [menuSearch, setMenuSearch] = useState('')

  const effectiveTenantId = role?.isSystemRole ? null : tenantId
  const flatMenus = useMemo(() => flattenMenus(menus), [menus])

  const filteredMenus = useMemo(() => {
    if (!menuSearch.trim()) return menus
    const q = menuSearch.trim().toLowerCase()
    const filterTree = (items: MenuItem[]): MenuItem[] =>
      items
        .map((m) => ({
          ...m,
          children: m.children?.length ? filterTree(m.children) : [],
        }))
        .filter(
          (m) =>
            m.label.toLowerCase().includes(q) ||
            (m.route?.toLowerCase() ?? '').includes(q) ||
            m.children.length > 0
        )
    return filterTree(menus)
  }, [menus, menuSearch])

  const loadRole = useCallback(async () => {
    if (!isValidRoleId) return
    setLoadingRole(true)
    try {
      const r = await rolesApi.getById(roleId)
      setRole(r)
    } catch {
      toast({ title: 'Hata', description: 'Rol bilgisi yüklenemedi.', variant: 'danger' })
      setRole(null)
    } finally {
      setLoadingRole(false)
    }
  }, [roleId, isValidRoleId, toast])

  const loadMenus = useCallback(async () => {
    setLoadingMenus(true)
    try {
      const tree = await menuAPI.getTree(tenantId ?? undefined, true)
      setMenus(tree)
    } catch {
      toast({ title: 'Hata', description: 'Menüler yüklenemedi.', variant: 'danger' })
      setMenus([])
    } finally {
      setLoadingMenus(false)
    }
  }, [tenantId, toast])

  const loadRoleMenus = useCallback(async () => {
    if (!isValidRoleId || !role) return
    setLoadingRoleMenus(true)
    try {
      const res = await rolesApi.getRoleMenus(roleId, effectiveTenantId ?? null)
      setSelectedMenuIds(new Set(res.menuIds ?? []))
    } catch {
      setSelectedMenuIds(new Set())
    } finally {
      setLoadingRoleMenus(false)
    }
  }, [roleId, effectiveTenantId, role, isValidRoleId])

  useEffect(() => {
    if (isValidRoleId) {
      loadRole()
      loadMenus()
    }
  }, [isValidRoleId, loadRole, loadMenus])

  useEffect(() => {
    if (role && menus.length > 0) {
      loadRoleMenus()
    } else if (role && !menus.length) {
      setSelectedMenuIds(new Set())
      setLoadingRoleMenus(false)
    }
  }, [role, menus.length, loadRoleMenus])

  const toggleMenu = (id: number, checked: boolean) => {
    setSelectedMenuIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const toggleParentAndChildren = (item: MenuItem, checked: boolean) => {
    const ids = flattenMenus([item]).map((m) => m.id)
    setSelectedMenuIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)))
      return next
    })
  }

  const selectAll = () => setSelectedMenuIds(new Set(flatMenus.map((m) => m.id)))
  const clearAll = () => setSelectedMenuIds(new Set())

  const handleSave = async () => {
    if (!role) return
    setSaving(true)
    try {
      await rolesApi.updateRoleMenus(roleId, {
        tenantId: effectiveTenantId ?? null,
        menuIds: Array.from(selectedMenuIds),
      })
      toast({ title: 'Kaydedildi', description: 'Rol menü atamaları güncellendi.', variant: 'success' })
      navigate(tenantId != null ? `/organization/roles?tenantId=${tenantId}` : '/organization/roles')
    } catch (e: unknown) {
      const err = e as { message?: string }
      toast({
        title: 'Hata',
        description: err?.message ?? 'Kaydetme başarısız.',
        variant: 'danger',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleBack = () => {
    navigate(tenantId != null ? `/organization/roles?tenantId=${tenantId}` : '/organization/roles')
  }

  const renderMenuRow = (item: MenuItem, depth: number) => {
    const hasChildren = !!(item.children?.length)
    const isChecked = selectedMenuIds.has(item.id)
    const childIds = hasChildren ? flattenMenus(item.children).map((m) => m.id) : []
    const allChildrenChecked =
      hasChildren && childIds.length > 0 && childIds.every((id) => selectedMenuIds.has(id))
    const IconComponent = getLucideIcon(item.icon)

    return (
      <React.Fragment key={item.id}>
        <div
          className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          style={{ paddingLeft: 16 + depth * 24 }}
        >
          <div className="flex-shrink-0 w-6 flex items-center justify-center">
            <input
              type="checkbox"
              checked={hasChildren ? Boolean(isChecked || allChildrenChecked) : isChecked}
              onChange={(e) =>
                hasChildren
                  ? toggleParentAndChildren(item, e.target.checked)
                  : toggleMenu(item.id, e.target.checked)
              }
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-primary-500 focus:ring-primary-500 cursor-pointer"
              aria-label={item.label || 'Seç'}
            />
          </div>
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-gray-600 dark:text-gray-400">
            {IconComponent ? (
              <IconComponent className="w-4 h-4" />
            ) : item.icon ? (
              <i className="material-symbols-outlined text-base">{item.icon}</i>
            ) : (
              <span className="text-gray-400 text-xs">-</span>
            )}
          </div>
          <div className="flex-1 min-w-32 text-sm text-gray-700 dark:text-gray-300 truncate">
            {item.label || '—'}
          </div>
          <div className="flex-1 min-w-32 text-sm text-gray-500 dark:text-gray-400 truncate hidden sm:block">
            {item.description || '—'}
          </div>
          <div className="flex-1 min-w-40 text-sm text-gray-500 dark:text-gray-400 truncate">
            {item.route || '—'}
          </div>
        </div>
        {hasChildren ? item.children!.map((child) => renderMenuRow(child, depth + 1)) : null}
      </React.Fragment>
    )
  }

  if (!isValidRoleId) {
    return (
      <div className="p-6">
        <PageHeader title="Menü Yönetimi" />
        <Card className="p-6">
          <p className="text-gray-500">Geçersiz rol. Lütfen roller listesinden menü yönetimine girin.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/organization/roles')}>
            Rollere dön
          </Button>
        </Card>
      </div>
    )
  }

  const loading = loadingRole || loadingMenus || loadingRoleMenus

  return (
    <div className="p-6 space-y-6">
      <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
        <Link
          to={tenantId != null ? `/organization/roles?tenantId=${tenantId}` : '/organization/roles'}
          className="hover:text-primary-600 dark:hover:text-primary-400"
        >
          Roller
        </Link>
        <span aria-hidden>/</span>
        <span className="text-gray-900 dark:text-gray-100 font-medium">
          {role ? `${role.name} (ID: ${role.roleId})` : 'Yükleniyor…'}
        </span>
      </nav>
      <PageHeader
        title="Menü Yönetimi"
        subtitle={role ? `Rol: ${role.name}` : undefined}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        {/* Sol: menü ağacı */}
        <Card className="p-4 flex flex-col min-h-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                Menüler
              </span>
              <Badge variant="info" className="shrink-0">
                {selectedMenuIds.size} seçili
              </Badge>
              <Input
                placeholder="Menü ara…"
                value={menuSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMenuSearch(e.target.value)}
                className="max-w-[200px] ml-auto"
              />
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={selectAll} disabled={loadingMenus}>
                Tümünü seç
              </Button>
              <Button variant="outline" size="sm" onClick={clearAll} disabled={loadingMenus}>
                Temizle
              </Button>
            </div>
          </div>

          {loadingMenus ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : menus.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <i className="material-symbols-outlined text-4xl mb-2 opacity-50">menu</i>
              <p>Menü bulunamadı.</p>
            </div>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
              <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-3 text-sm font-semibold bg-gray-100 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <div className="flex-shrink-0 w-6" />
                <div className="w-6 h-6 flex-shrink-0" />
                <div className="flex-1 min-w-32">Label</div>
                <div className="flex-1 min-w-32 hidden sm:block">Açıklama</div>
                <div className="flex-1 min-w-40">Route</div>
              </div>
              <div className="overflow-y-auto max-h-[60vh]">
                {filteredMenus.map((item) => renderMenuRow(item, 0))}
              </div>
            </div>
          )}
        </Card>

        {/* Sağ: rol bilgisi + aksiyonlar */}
        <div className="space-y-4">
          <Card className="p-4 h-fit">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-3">
              Rol Bilgisi
            </label>
            {loadingRole ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : role ? (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">ID:</span>
                  <span className="font-mono font-medium">{role.roleId}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Ad:</span>
                  <span className="font-medium">{role.name}</span>
                </div>
                {role.code && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">Kod:</span>
                    <span>{role.code}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Tür:</span>
                  <Badge variant={role.isSystemRole ? 'default' : 'secondary'}>
                    {role.isSystemRole ? 'Sistem' : 'Tenant'}
                  </Badge>
                </div>
                {role.description && (
                  <p className="text-gray-500 text-xs pt-1">{role.description}</p>
                )}
              </div>
            ) : (
              <p className="text-gray-500">Rol yüklenemedi.</p>
            )}
          </Card>

          <Card className="p-4">
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleSave}
                loading={saving}
                disabled={!role || saving || loading}
                className="w-full"
              >
                Kaydet
              </Button>
              <Button variant="outline" onClick={handleBack} disabled={saving} className="w-full">
                Vazgeç / Geri dön
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {loading && !loadingRole && (
        <div className="flex items-center justify-center py-4 text-sm text-gray-500">
          <Spinner className="mr-2 h-4 w-4" />
          Rol menü atamaları yükleniyor…
        </div>
      )}
    </div>
  )
}
